import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, RefreshCw, Loader2, Users, GraduationCap, KeyRound, Ticket,
  IndianRupee, TrendingUp, Search, CheckCircle2, Circle, BarChart3, Mail,
  KeyRound as ResetIcon, Ban, RotateCcw, ShieldAlert, Phone, X, Eye, EyeOff, Copy, CalendarCheck,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import {
  getPartnerBatchAnalytics, sendStudentResetLink,
  revokeStudentAccess, reassignStudentAccess,
} from '@/api/stnApi';
import StudentDetailDrawer from '@/components/partner/StudentDetailDrawer';
import AttendanceMarkModal from '@/components/partner/AttendanceMarkModal';
import CouponReshareModal from '@/components/partner/CouponReshareModal';
import ResendOnboardingModal from '@/components/partner/ResendOnboardingModal';

const SUPPORT_NUMBER = '9205919126';
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';
const scoreCls = (s) => s >= 75 ? 'text-emerald-500' : s >= 50 ? 'text-amber-500' : 'text-rose-500';

export default function AssignedBatchAnalytics() {
  const { batchId } = useParams();
  const navigate = useNavigate();
  const [data, setData]   = useState(null);
  const [loading, setLoad] = useState(true);
  const [search, setSearch] = useState('');
  const [program, setProgram] = useState('all');
  const [busyId, setBusyId] = useState(null);          // studentId an action is running on
  const [confirm, setConfirm] = useState(null);        // { student } for revoke confirm
  const [revokeReason, setRevokeReason] = useState('');
  const [detailId, setDetailId] = useState(null);      // studentId for detail drawer
  const [showAll, setShowAll] = useState(false);       // reveal all secrets (coupon + temp pwd)
  const [attendOpen, setAttendOpen] = useState(false); // attendance marking modal
  const [couponOpen, setCouponOpen] = useState(false); // onboarding/coupon reshare modal
  const [resendStudent, setResendStudent] = useState(null); // per-intern resend-onboarding modal
  const [revealed, setRevealed] = useState(new Set()); // individually revealed "id:field"
  const isShown = (id, field) => showAll || revealed.has(`${id}:${field}`);
  const toggleReveal = (id, field) => setRevealed((p) => {
    const n = new Set(p); const k = `${id}:${field}`; n.has(k) ? n.delete(k) : n.add(k); return n;
  });
  const copy = (v) => { navigator.clipboard?.writeText(v); toast.success('Copied'); };

  const load = () => {
    setLoad(true);
    getPartnerBatchAnalytics(batchId)
      .then(setData)
      .catch((e) => toast.error(e?.message || 'Could not load analytics'))
      .finally(() => setLoad(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [batchId]);

  const s = data?.summary || {};
  // Every program bound to the batch (from the backend) unioned with any program
  // interns are enrolled in — so the filter lists the full batch catalogue, not
  // just the programs that already have enrolments.
  const allPrograms = useMemo(() => {
    const set = new Set(data?.batchPrograms || []);
    (data?.students || []).forEach((r) => (r.programs || []).forEach((p) => set.add(p)));
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [data]);

  const students = useMemo(() => (data?.students || []).filter((r) => {
    if (program !== 'all' && !(r.programs || []).includes(program)) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    return r.name?.toLowerCase().includes(q) || r.email?.toLowerCase().includes(q)
      || String(r.mobile || '').includes(q) || r.college?.toLowerCase().includes(q);
  }), [data, search, program]);

  // ── Per-student actions ──────────────────────────────────────────────────
  const patchStudent = (sid, patch) =>
    setData((d) => ({ ...d, students: d.students.map((r) => r.studentId === sid ? { ...r, ...patch } : r) }));

  // Open the per-intern resend dialog (shows coupon + lets you extend the deadline).
  const doResend = (r) => setResendStudent(r);
  const doResetLink = async (r) => {
    setBusyId(r.studentId);
    try { const res = await sendStudentResetLink(batchId, r.studentId); toast.success(res?.message || 'Reset link sent'); }
    catch (e) { toast.error(e?.message || 'Failed'); } finally { setBusyId(null); }
  };
  const doRevoke = async () => {
    if (!confirm) return;
    const r = confirm.student;
    setBusyId(r.studentId);
    try {
      await revokeStudentAccess(batchId, r.studentId, revokeReason);
      patchStudent(r.studentId, { revoked: true, revokedReason: revokeReason });
      toast.success('Access paused');
      setConfirm(null); setRevokeReason('');
    } catch (e) { toast.error(e?.message || 'Failed'); } finally { setBusyId(null); }
  };
  const doReassign = async (r) => {
    setBusyId(r.studentId);
    try {
      await reassignStudentAccess(batchId, r.studentId);
      patchStudent(r.studentId, { revoked: false, revokedReason: '' });
      toast.success('Access restored — intern resumes where they left off');
    } catch (e) { toast.error(e?.message || 'Failed'); } finally { setBusyId(null); }
  };

  const KPIS = [
    { icon: Users, label: 'Registered', value: s.totalRegistered },
    { icon: CheckCircle2, label: 'Activated', value: s.activated },
    { icon: KeyRound, label: 'Reset pwd', value: s.passwordReset },
    { icon: GraduationCap, label: 'Enrolled', value: s.enrolled },
    { icon: Ticket, label: 'Used coupon', value: s.couponConsumers },
    { icon: IndianRupee, label: '₹ saved', value: `₹${(s.totalCouponSaved || 0).toLocaleString('en-IN')}` },
  ];

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <button onClick={() => navigate('/partner-batches')} className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-4">
        <ArrowLeft className="w-4 h-4" /> Back to Partner Batches
      </button>

      <div className="flex items-center justify-between gap-3 mb-1 flex-wrap">
        <h1 className="text-2xl font-bold flex items-center gap-2"><BarChart3 className="w-6 h-6 text-primary" /> {data?.batchTitle || 'Batch analytics'}</h1>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={() => setCouponOpen(true)} className="text-orange-600 border-orange-500/30 hover:bg-orange-500/10">
            <Ticket className="w-4 h-4 mr-1.5" /> Reshare onboarding
          </Button>
          <Button variant="outline" size="sm" onClick={() => setAttendOpen(true)} className="text-emerald-600 border-emerald-500/30 hover:bg-emerald-500/10">
            <CalendarCheck className="w-4 h-4 mr-1.5" /> Attendance
          </Button>
          <Button variant="outline" size="sm" onClick={load} disabled={loading}>
            <RefreshCw className={cn('w-4 h-4 mr-1.5', loading && 'animate-spin')} /> Refresh
          </Button>
        </div>
      </div>
      <p className="text-sm text-muted-foreground mb-6">Detailed registration & engagement report{data?.coupon ? ` · Coupon ${data.coupon.code}` : ''}</p>

      {loading ? (
        <div className="flex items-center justify-center gap-2 py-20 text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin" /> Loading analytics…</div>
      ) : data?.studentsHidden ? (
        <Card><CardContent className="flex flex-col items-center py-16 text-muted-foreground">
          <ShieldAlert className="w-10 h-10 mb-3 opacity-50" />
          <p className="font-medium text-foreground">Student roster not shared</p>
          <p className="text-sm mt-1">Contact the NexusAI team to enable student-level visibility.</p>
        </CardContent></Card>
      ) : (
        <>
          {/* KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-4">
            {KPIS.map((k) => (
              <Card key={k.label}><CardContent className="p-3">
                <k.icon className="w-4 h-4 text-primary mb-1.5" />
                <p className="text-lg font-bold leading-none">{k.value ?? 0}</p>
                <p className="text-[10px] text-muted-foreground mt-1">{k.label}</p>
              </CardContent></Card>
            ))}
          </div>
          <div className="flex items-center gap-4 text-xs text-muted-foreground mb-5">
            <span className="flex items-center gap-1"><TrendingUp className="w-3.5 h-3.5" /> Avg assignment: <b className={scoreCls(s.avgAssignmentScore)}>{s.avgAssignmentScore}%</b></span>
            <span>· Avg attendance: <b className="text-foreground">{s.avgAttendance}%</b></span>
          </div>

          {/* Toolbar */}
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <div className="relative flex-1 min-w-[240px] max-w-sm">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name, email, phone, college…" className="pl-8 h-9" />
            </div>
            <select value={program} onChange={(e) => setProgram(e.target.value)} className="h-9 rounded-lg border border-border bg-card text-sm px-2">
              <option value="all">All programs ({allPrograms.length})</option>
              {allPrograms.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
            <Button variant="outline" size="sm" onClick={() => { setShowAll((v) => !v); setRevealed(new Set()); }} title="Reveal / hide coupon codes & temp passwords">
              {showAll ? <><EyeOff className="w-4 h-4 mr-1.5" /> Hide all</> : <><Eye className="w-4 h-4 mr-1.5" /> View all</>}
            </Button>
            <span className="text-xs text-muted-foreground ml-auto">{students.length} interns</span>
          </div>

          {/* Roster */}
          <Card><CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-xs min-w-[900px]">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-wider text-muted-foreground border-b border-border">
                    <th className="px-3 py-2.5">Intern</th>
                    <th className="px-2 py-2.5">Programs</th>
                    <th className="px-2 py-2.5 text-center">Activated</th>
                    <th className="px-2 py-2.5 text-center">Coupon</th>
                    <th className="px-2 py-2.5">Coupon code</th>
                    <th className="px-2 py-2.5">Temp pwd</th>
                    <th className="px-2 py-2.5 text-right">INXP</th>
                    <th className="px-2 py-2.5 text-center">Attend</th>
                    <th className="px-2 py-2.5 text-center">Assign AI</th>
                    <th className="px-2 py-2.5 text-center">Quiz</th>
                    <th className="px-2 py-2.5 text-center">Access</th>
                    <th className="px-3 py-2.5 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {students.map((r) => {
                    const busy = busyId === r.studentId;
                    return (
                      <tr key={r.studentId} className={cn('border-b border-border/60 hover:bg-muted/30', r.revoked && 'bg-rose-500/[0.04]')}>
                        <td className="px-3 py-2.5">
                          <button onClick={() => setDetailId(r.studentId)} className="font-semibold text-foreground hover:text-primary text-left">{r.name}</button>
                          {r.selfEnrolled
                            ? <p className="text-[10px] text-muted-foreground italic" title={r.contactNote}>Joined on their own · contact not shared</p>
                            : <p className="text-[10px] text-muted-foreground">{r.email}{r.mobile ? ` · ${r.mobile}` : ''}</p>}
                          {r.college && <p className="text-[10px] text-muted-foreground/70 truncate max-w-[180px]">{r.college}</p>}
                        </td>
                        <td className="px-2 py-2.5 text-muted-foreground max-w-[160px]">
                          <span className="line-clamp-2">{(r.programs || []).join(', ') || '—'}</span>
                        </td>
                        <td className="px-2 py-2.5 text-center">{r.activated ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 inline" /> : <Circle className="w-3.5 h-3.5 text-muted-foreground/40 inline" />}</td>
                        <td className="px-2 py-2.5 text-center">{r.couponUses > 0 ? <span className="text-[10px] font-semibold text-amber-500">{r.couponUses}× · ₹{r.couponSaved}</span> : <span className="text-muted-foreground/40">—</span>}</td>
                        <td className="px-2 py-2.5"><Secret value={r.couponCode} shown={isShown(r.studentId, 'coupon')} onToggle={() => toggleReveal(r.studentId, 'coupon')} onCopy={copy} /></td>
                        <td className="px-2 py-2.5"><Secret value={r.tempPassword} shown={isShown(r.studentId, 'pwd')} onToggle={() => toggleReveal(r.studentId, 'pwd')} onCopy={copy} mono /></td>
                        <td className="px-2 py-2.5 text-right font-medium">{(r.inxp || 0).toLocaleString()}</td>
                        <td className="px-2 py-2.5 text-center">{r.attendancePct}%</td>
                        <td className={cn('px-2 py-2.5 text-center font-semibold', scoreCls(r.avgAssignmentScore))}>{r.assignmentsSubmitted ? `${r.avgAssignmentScore}%` : '—'}</td>
                        <td className="px-2 py-2.5 text-center">{r.quizzesTaken ? `${r.quizzesPassed}/${r.quizzesTaken}` : '—'}</td>
                        <td className="px-2 py-2.5 text-center">
                          {r.revoked
                            ? <Badge variant="outline" className="bg-rose-500/10 text-rose-500 border-rose-500/25 text-[10px]">Paused</Badge>
                            : <Badge variant="outline" className="bg-emerald-500/10 text-emerald-500 border-emerald-500/25 text-[10px]">Active</Badge>}
                        </td>
                        <td className="px-3 py-2.5">
                          <div className="flex items-center justify-end gap-1">
                            <IconBtn title="View detailed report" onClick={() => setDetailId(r.studentId)} disabled={busy}><Eye className="w-3.5 h-3.5" /></IconBtn>
                            <IconBtn title="Resend onboarding email" onClick={() => doResend(r)} disabled={busy}><Mail className="w-3.5 h-3.5" /></IconBtn>
                            <IconBtn title="Send reset link" onClick={() => doResetLink(r)} disabled={busy}><ResetIcon className="w-3.5 h-3.5" /></IconBtn>
                            {r.revoked
                              ? <IconBtn title="Reassign / restore access" onClick={() => doReassign(r)} disabled={busy} tone="emerald"><RotateCcw className="w-3.5 h-3.5" /></IconBtn>
                              : <IconBtn title="Revoke access" onClick={() => { setConfirm({ student: r }); setRevokeReason(''); }} disabled={busy} tone="rose"><Ban className="w-3.5 h-3.5" /></IconBtn>}
                            {busy && <Loader2 className="w-3.5 h-3.5 animate-spin text-muted-foreground" />}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  {students.length === 0 && <tr><td colSpan={12} className="text-center py-10 text-muted-foreground">No interns match your filters.</td></tr>}
                </tbody>
              </table>
            </div>
          </CardContent></Card>
        </>
      )}

      {/* Revoke confirm popup */}
      {confirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setConfirm(null)}>
          <div className="bg-card rounded-2xl w-full max-w-md p-6 border border-border" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-lg font-bold flex items-center gap-2 text-rose-500"><Ban className="w-5 h-5" /> Revoke access</h3>
              <button onClick={() => setConfirm(null)} className="text-muted-foreground hover:text-foreground"><X className="w-5 h-5" /></button>
            </div>
            <p className="text-sm text-muted-foreground mb-1">Pause access for <b className="text-foreground">{confirm.student.name}</b>?</p>
            <p className="text-xs text-muted-foreground mb-3">They won't be able to sign in and will be asked to contact the NexusAI team at <b>{SUPPORT_NUMBER}</b>. Their progress is preserved — you can reassign anytime to let them resume.</p>
            <Input value={revokeReason} onChange={(e) => setRevokeReason(e.target.value)} placeholder="Reason (optional)" className="h-9 mb-4" />
            <div className="flex items-center justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setConfirm(null)}>Cancel</Button>
              <Button size="sm" className="bg-rose-500 hover:bg-rose-600 text-white" onClick={doRevoke} disabled={busyId === confirm.student.studentId}>
                {busyId === confirm.student.studentId ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Ban className="w-4 h-4 mr-1" />} Revoke
              </Button>
            </div>
          </div>
        </div>
      )}

      {detailId && (
        <StudentDetailDrawer batchId={batchId} studentId={detailId} onClose={() => setDetailId(null)}
          onAction={{ resend: (r) => { setDetailId(null); setResendStudent(r); }, resetLink: doResetLink, revoke: (r) => { setDetailId(null); setConfirm({ student: r }); }, reassign: doReassign }} />
      )}
      {resendStudent && (
        <ResendOnboardingModal batchId={batchId} studentId={resendStudent.studentId} studentName={resendStudent.name}
          onClose={() => setResendStudent(null)} onDone={load} />
      )}
      {attendOpen && (
        <AttendanceMarkModal batchId={batchId} batchName={data?.batchTitle} onClose={() => { setAttendOpen(false); load(); }} />
      )}
      {couponOpen && (
        <CouponReshareModal batchId={batchId} batchName={data?.batchTitle} onClose={() => { setCouponOpen(false); load(); }} />
      )}
    </div>
  );
}

// Masked secret cell with per-row reveal + copy.
const Secret = ({ value, shown, onToggle, onCopy, mono }) => {
  if (!value) return <span className="text-muted-foreground/40">—</span>;
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={cn('text-[11px]', mono && 'font-mono')}>{shown ? value : '••••••'}</span>
      <button onClick={onToggle} title={shown ? 'Hide' : 'Reveal'} className="text-muted-foreground hover:text-foreground">
        {shown ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
      </button>
      {shown && <button onClick={() => onCopy(value)} title="Copy" className="text-muted-foreground hover:text-foreground"><Copy className="w-3.5 h-3.5" /></button>}
    </span>
  );
};

const IconBtn = ({ children, title, onClick, disabled, tone }) => (
  <button title={title} onClick={onClick} disabled={disabled}
    className={cn('h-7 w-7 inline-flex items-center justify-center rounded-md border border-border hover:bg-muted disabled:opacity-40',
      tone === 'rose' && 'text-rose-500 hover:bg-rose-500/10', tone === 'emerald' && 'text-emerald-500 hover:bg-emerald-500/10',
      !tone && 'text-muted-foreground')}>
    {children}
  </button>
);
